// Need Help — API layer.
//
// Reuses the endpoints that already exist for the chat and voice agents:
//   POST /Chatbot/create-chat-session      { agent_id }
//   POST /Chatbot/create-chat-completion   { chat_id, content, chat_agent_id, call_id? }
//   POST /agent/createWidegetWebCall       { agent_id, url, chat_id? }
// plus three Need Help endpoints:
//   POST /Chatbot/need-help/chat-status    { chat_id }   (auth required)
//   POST /Chatbot/need-help/register-chat  { chat_id }   (auth required)
//   POST /Chatbot/need-help/chat-history   {}            (auth required)
//
// No retell_llm_dynamic_variables are ever sent. The only context that travels
// between chat and voice is the previous session's id (call_id / chat_id), which
// the backend uses to fetch that one session's summary from Retell.

import { API_BASE_URL } from "../Store/apiStore";

export class NeedHelpApiError extends Error {
  constructor(message, { status = 0, data = null } = {}) {
    super(message);
    this.name = "NeedHelpApiError";
    this.status = status;
    this.data = data;
  }
}

const request = async (path, { body, token } = {}) => {
  let res;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body || {}),
    });
  } catch (_e) {
    throw new NeedHelpApiError("Network error", { status: 0, data: null });
  }

  let data = null;
  try {
    data = await res.json();
  } catch (_e) {
    /* empty or non-JSON body */
  }

  if (!res.ok) {
    const message =
      data?.error ||
      data?.message ||
      data?.detail?.message ||
      `HTTP ${res.status}`;
    throw new NeedHelpApiError(String(message), { status: res.status, data });
  }
  return data;
};

/**
 * @typedef {Object} NeedHelpTicketInput
 * @property {string} subject
 * @property {string} description
 * @property {string} userId
 * @property {File[]} [attachments]
 * @property {string} token
 */

/**
 * Creates a ticket from the Need Help modal, including optional attachments.
 * @param {NeedHelpTicketInput} params
 */
export const createNeedHelpTicket = async ({
  subject,
  description,
  userId,
  attachments = [],
  token,
}) => {
  const payload = new FormData();
  payload.append("subject", subject);
  payload.append("category", "Need Help");
  payload.append("description", description);
  payload.append("userId", userId);
  attachments.forEach((attachment) => {
    payload.append("ticketAttachments", attachment);
  });

  let res;
  try {
    res = await fetch(`${API_BASE_URL}/tickets/create_ticket`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: payload,
    });
  } catch (_e) {
    throw new NeedHelpApiError("Network error", { status: 0, data: null });
  }

  let data = null;
  try {
    data = await res.json();
  } catch (_e) {
    /* empty or non-JSON body */
  }

  if (!res.ok || !data?.success) {
    const message =
      data?.error ||
      data?.message ||
      `Ticket submission failed${res.status ? ` (HTTP ${res.status})` : ""}`;
    throw new NeedHelpApiError(String(message), { status: res.status, data });
  }

  return data;
};

/**
 * True when a failed chat request means "this chat session has ended".
 * Our own configuration / limit errors are not an ended session, so they are
 * excluded and surface as a normal failure instead.
 */
export const isSessionEndedError = (err) => {
  if (!err) return false;
  const data = err.data || {};
  if (data.error === "Agent not found" || data.upgradeRequired) return false;
  if (err.status === 404 || err.status === 410) return true;

  let text = "";
  try {
    text = JSON.stringify(data).toLowerCase();
  } catch (_e) {
    text = String(err.message || "").toLowerCase();
  }
  return /(already ended|has ended|\bended\b|expired|closed|not found|invalid chat|no chat)/.test(
    text,
  );
};

// ADDED: tells the backend which logged-in user owns a new chat, so the chat_ended
// webhook can save the user id with the chat. Best-effort and not awaited: if it
// fails (or there is no token), the chat works exactly the same and the saved
// chat just has no user id.
const registerChatOwner = (chatId) => {
  let token = "";
  try {
    token = localStorage.getItem("token") || "";
  } catch (_e) {
    /* storage unavailable */
  }
  if (!token || !chatId) return;

  request("/Chatbot/need-help/register-chat", {
    body: { chat_id: chatId },
    token,
  }).catch(() => {
    /* ignored on purpose, see above */
  });
};

/** Starts a chat session. Sends the last call id when there is one. */
export const createChatSession = async ({ chatAgentId, callId }) => {
  const data = await request("/Chatbot/create-chat-session", {
    body: {
      agent_id: chatAgentId,
    },
  });
  if (!data?.chat_id) {
    throw new NeedHelpApiError("chat_id missing in response", {
      status: 502,
      data,
    });
  }
  // ADDED: tag this chat with the logged-in user (fire and forget).
  registerChatOwner(data.chat_id);
  return data.chat_id;
};

/**
 * Sends one user message.
 *   -> { text, callContextSent }
 * `callId` is optional. When it is empty or missing, the request body has no
 * call_id key at all. `callContextSent` is false only when the backend tried to
 * give the chat the call's context and failed.
 */
export const sendChatMessage = async ({
  chatId,
  chatAgentId,
  content,
  callId,
}) => {
  const data = await request("/Chatbot/create-chat-completion", {
    body: {
      chat_id: chatId,
      content,
      chat_agent_id: chatAgentId,
      ...(callId ? { call_id: callId } : {}),
    },
  });
  return {
    text: typeof data?.text === "string" ? data.text : "",
    callContextSent: data?.callContextSent !== false,
  };
};

/** Asks our backend (which asks Retell) whether a chat session is still open. */
export const getChatStatus = async ({ chatId, token }) => {
  const data = await request("/Chatbot/need-help/chat-status", {
    body: { chat_id: chatId },
    token,
  });
  return { open: data?.open === true };
};

// ADDED
/**
 * One page of the logged-in user's saved Need Help chats, NEWEST first.
 * `search` is optional; `offset` is the `nextOffset` of the previous page.
 *   -> { chats: [{ chatId, startedAt, endedAt, messages }], hasMore, nextOffset }
 */
export const getChatHistory = async ({
  token,
  search = "",
  offset = 0,
  limit = 20,
}) => {
  const data = await request("/Chatbot/need-help/chat-history", {
    body: { search, offset, limit },
    token,
  });
  const chats = Array.isArray(data?.chats) ? data.chats : [];
  return {
    chats,
    hasMore: data?.hasMore === true,
    nextOffset: Number.isFinite(data?.nextOffset)
      ? data.nextOffset
      : offset + chats.length,
  };
};

/**
 * Creates a voice web call. Sends the last chat id when there is one; the
 * backend uses it to carry the chat's context into the call and to end that
 * chat. `url` is required by the existing endpoint's domain check.
 */
export const createVoiceCall = async ({ voiceAgentId, chatId }) => {
  const data = await request("/agent/createWidegetWebCall", {
    body: {
      agent_id: voiceAgentId,
      url: window.location.origin,
      ...(chatId ? { chat_id: chatId } : {}),
    },
  });
  if (data?.mode === "python") {
    throw new NeedHelpApiError("Python voice agents are not supported here", {
      status: 400,
      data,
    });
  }
  if (!data?.access_token || !data?.call_id) {
    throw new NeedHelpApiError("Call token missing in response", {
      status: 502,
      data,
    });
  }
  return data;
};

/** All tickets of the logged-in user (no pagination). Returns an array. */
export const getNeedHelpTickets = async ({ userId, token }) => {
  let res;
  try {
    res = await fetch(
      `${API_BASE_URL}/tickets/get_tickets_by_user?userId=${encodeURIComponent(userId || "")}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
  } catch (_e) {
    throw new NeedHelpApiError("Network error", { status: 0, data: null });
  }

  let data = null;
  try {
    data = await res.json();
  } catch (_e) {
    /* empty or non-JSON body */
  }

  if (!res.ok || !data?.success) {
    throw new NeedHelpApiError(
      String(data?.message || `HTTP ${res.status}`),
      { status: res.status, data },
    );
  }
  return Array.isArray(data.tickets) ? data.tickets : [];
};

/** Chats of one ticket. -> array of { sentBy, message, isRead, timestamp } */
export const getTicketChats = async ({ ticketId, userId, token }) => {
  let res;
  try {
    res = await fetch(
      `${API_BASE_URL}/tickets/get_ticket_chats?ticketId=${encodeURIComponent(ticketId)}&userId=${encodeURIComponent(userId || "")}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
  } catch (_e) {
    throw new NeedHelpApiError("Network error", { status: 0, data: null });
  }

  let data = null;
  try {
    data = await res.json();
  } catch (_e) {
    /* empty or non-JSON body */
  }

  if (!res.ok || !data?.success) {
    throw new NeedHelpApiError(String(data?.message || `HTTP ${res.status}`), {
      status: res.status,
      data,
    });
  }
  return Array.isArray(data.chats) ? data.chats : [];
};

/** Sends one user message. -> the saved message */
export const sendTicketChat = async ({ ticketId, userId, message, token }) => {
  const data = await request("/tickets/send_ticket_chat", {
    body: { ticketId, userId, message },
    token,
  });
  if (!data?.chat) {
    throw new NeedHelpApiError("chat missing in response", { status: 502, data });
  }
  return data.chat;
};

/** Uploads more attachments to an existing ticket. -> the full updated attachments array */
export const addTicketAttachments = async ({ ticketId, userId, files, token }) => {
  const payload = new FormData();
  payload.append("ticketId", ticketId);
  payload.append("userId", userId);
  files.forEach((file) => payload.append("ticketAttachments", file));

  let res;
  try {
    res = await fetch(`${API_BASE_URL}/tickets/add_ticket_attachments`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: payload,
    });
  } catch (_e) {
    throw new NeedHelpApiError("Network error", { status: 0, data: null });
  }

  let data = null;
  try {
    data = await res.json();
  } catch (_e) {
    /* empty or non-JSON body */
  }

  if (!res.ok || !data?.success) {
    throw new NeedHelpApiError(
      String(data?.message || `Upload failed${res.status ? ` (HTTP ${res.status})` : ""}`),
      { status: res.status, data },
    );
  }
  return Array.isArray(data.attachments) ? data.attachments : [];
};

/** Deletes one attachment of a ticket. -> the full updated attachments array */
export const deleteTicketAttachment = async ({ ticketId, userId, url, token }) => {
  const data = await request("/tickets/delete_ticket_attachment", {
    body: { ticketId, userId, url },
    token,
  });
  return Array.isArray(data?.attachments) ? data.attachments : [];
};
